import React, { useMemo, useState } from 'react';
import { rememberUpsellToken } from '../../../../../../../utils/upsellToken';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  Switch,
  alpha
} from '@mui/material';
import {
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { publicIp } from 'public-ip';
import { useNetwork } from '../../../../../../../utils/network';
import { useShowSnackbar } from '../../../../../../../utils/snackbar';
import { track, trackError } from '../../../../../../../utils/track';
import {
  META_PIXEL_ID,
  createMetaRegistrationEventId,
  getMetaEventSourceUrl,
  loadMetaPixel,
  trackCompleteRegistration
} from '../../../../../../../utils/metaPixel';
import BoltIcon from '@mui/icons-material/Bolt';
import StarsIcon from '@mui/icons-material/Stars';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const visaIcon = '/payment-icons/visa.svg';
const mastercardIcon = '/payment-icons/mastercard.svg';
const stripeIcon = '/payment-icons/stripe.svg';

const glassCardSx = {
  borderRadius: 4,
  border: '1px solid rgba(18, 31, 38, 0.10)',
  background: '#ffffff',
  boxShadow: '0 24px 64px rgba(18, 31, 38, 0.10)',
  overflow: 'hidden'
};

const miniTileSx = {
  borderRadius: 3,
  border: '1px solid rgba(18,31,38,0.10)',
  background: '#f8fafc',
  p: 2.0,
  textAlign: 'left'
};

const bonusSwitchSx = {
  width: 62,
  height: 36,
  p: 0,
  display: 'flex',
  '& .MuiSwitch-switchBase': {
    p: 0.5,
    transitionDuration: '250ms',
    color: '#0f172a',
    '&.Mui-checked': {
      transform: 'translateX(26px)',
      color: '#061214',
      '& + .MuiSwitch-track': {
        background:
          'linear-gradient(90deg, rgba(124,255,250,0.55) 0%, rgba(124,255,250,0.95) 100%)',
        opacity: 1,
        border: '1px solid rgba(124,255,250,0.35)'
      }
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 28,
    height: 28,
    boxShadow: 'none'
  },
  '& .MuiSwitch-track': {
    borderRadius: 999,
    background: 'rgba(17,24,39,0.14)',
    opacity: 1,
    border: '1px solid rgba(17,24,39,0.12)'
  }
};

/* The same three on the dark surface — the promo pack's standalone checkout
 * (`features/PromoPack`), the first dark caller of the bonus block. Only the
 * colours differ; the unchecked thumb turns light so it shows on a dark track. */
const darkGlassCardSx = {
  ...glassCardSx,
  border: '1px solid rgba(140,170,190,0.16)',
  background: 'rgba(15,22,32,0.92)',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)'
};

const darkMiniTileSx = {
  ...miniTileSx,
  border: '1px solid rgba(140,170,190,0.16)',
  background: 'rgba(255,255,255,0.035)'
};

const darkBonusSwitchSx = {
  ...bonusSwitchSx,
  '& .MuiSwitch-switchBase': {
    ...bonusSwitchSx['& .MuiSwitch-switchBase'],
    color: '#e8f4fb'
  },
  '& .MuiSwitch-track': {
    ...bonusSwitchSx['& .MuiSwitch-track'],
    background: 'rgba(140,170,190,0.22)',
    border: '1px solid rgba(140,170,190,0.28)'
  }
};

type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  tokens?: number;
  description: string;
  dotColor: string;
  bonus?: {
    description: string;
    extraCards: number;
    extraEntries: number;
    price: number;
  };
};

type EntryFormValues = {
  fullName: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  countryIso2: string;
  countryName: string;
  stateIso2: string;
  stateName: string;
  phoneCountryCode: string;
  agreeToTerms: boolean;
  eligible: 'yes' | 'no' | '';
};

type RafflePaymentPayload = {
  entry: EntryFormValues;
  selectedPack: PackItem;
  standaloneItems: PackItem[];
  subscriptionItems: PackItem[];
  raffleUnlockToken?: string | null;
  signupCodeBonusEntries?: number;
  signupCodeBonusSource?: 'giveaway_subs' | 'signup_code';
  signupSource?: 'giveaway' | 'giveaway-subs' | 'giveaway-offer' | 'promo-pack';
};

type CheckoutMode = 'standalone' | 'subscription';

const money = (n: number | string | undefined) => {
  const x = Number(n ?? 0);
  if (!Number.isFinite(x)) return String(n ?? '');
  return x.toFixed(x % 1 === 0 ? 0 : 2);
};

/* The two surfaces this form can sit on.
 *
 * `light` reproduces the original palette EXACTLY, and it is the default, so
 * RafflePage, RafflePaymentPage, EmailPromo and PartnerCheckoutDialog render
 * byte-identically to before this prop existed. `dark` is
 * `features/GiveawayOffer/giveawayOfferTheme`'s palette, for the QR funnel whose
 * checkout dialog sits on a dark page.
 *
 * THE BONUS-UPSELL BLOCK HAS ITS OWN PALETTE, `BONUS_SURFACES` below. It renders
 * for `mode === 'standalone'` only, which had no dark caller until the promo pack
 * (`features/PromoPack`); its `light` side is the block's original literals and
 * the `glassCardSx` / `miniTileSx` / `bonusSwitchSx` objects themselves, so the
 * light standalone checkout renders exactly as before. */
const SURFACES = {
  light: {
    shell: '#f8fafc',
    panel: '#ffffff',
    inset: '#f8fafc',
    border: 'rgba(18,31,38,0.10)',
    borderSoft: 'rgba(18,31,38,0.08)',
    shadow: '0 18px 45px rgba(18,31,38,0.08)',
    text: '#111827',
    muted: 'rgba(17,24,39,0.58)',
    faint: 'rgba(17,24,39,0.52)',
    /* Three greys that are NOT the two above. They were separate literals before
       this palette existed, and collapsing them into `muted`/`faint` silently
       shifted the live checkout by a few percent of opacity. Kept distinct so the
       light surface reproduces the original exactly. */
    mutedIcon: 'rgba(17,24,39,0.55)',
    mutedStrong: 'rgba(17,24,39,0.62)',
    hint: 'rgba(17,24,39,0.50)',
    checkbox: '#94a3b8',
    checkboxOn: '#0891b2'
  },
  dark: {
    shell: 'rgba(12,18,26,0.72)',
    panel: 'rgba(15,22,32,0.92)',
    inset: 'rgba(255,255,255,0.035)',
    border: 'rgba(140,170,190,0.16)',
    borderSoft: 'rgba(140,170,190,0.12)',
    shadow: '0 18px 45px rgba(0,0,0,0.45)',
    text: '#e8f4fb',
    muted: '#8ea3b3',
    faint: '#8ea3b3',
    mutedIcon: '#8ea3b3',
    mutedStrong: '#8ea3b3',
    hint: '#8ea3b3',
    checkbox: '#5c6b78',
    checkboxOn: '#38e8ff'
  }
} as const;

/* The bonus-upsell block's palette (standalone only). Every `light` value is the
 * literal it replaced, character for character — including the spaced
 * `rgba(18, 31, 38, …)` inside `glassCardSx` — so the live giveaway checkout
 * does not move. `text` and `muted` happen to equal SURFACES.light's, but are
 * kept here so the block reads from one palette. */
const BONUS_SURFACES = {
  light: {
    card: glassCardSx,
    tile: miniTileSx,
    toggle: bonusSwitchSx,
    heading: '#0f172a',
    text: '#111827',
    muted: 'rgba(17,24,39,0.58)',
    chipFill: '#f8fafc',
    edge: '1px solid rgba(18,31,38,0.10)',
    rule: 'rgba(18,31,38,0.08)',
    price: '#0891b2',
    toggleFill: '#ffffff',
    toggleFillOn: 'rgba(8,145,178,0.10)',
    toggleTextOn: '#0f766e',
    toggleHint: 'rgba(51,65,85,0.72)'
  },
  dark: {
    card: darkGlassCardSx,
    tile: darkMiniTileSx,
    toggle: darkBonusSwitchSx,
    heading: '#e8f4fb',
    text: '#e8f4fb',
    muted: '#8ea3b3',
    chipFill: 'rgba(255,255,255,0.04)',
    edge: '1px solid rgba(140,170,190,0.16)',
    rule: 'rgba(140,170,190,0.12)',
    price: '#38e8ff',
    toggleFill: 'rgba(255,255,255,0.035)',
    toggleFillOn: 'rgba(56,232,255,0.10)',
    toggleTextOn: '#38e8ff',
    toggleHint: '#8ea3b3'
  }
};

export default function RaffleStripeCheckoutForm({
  payload,
  mode,
  pack,
  onPaid,
  subDiscount = 30,
  discountAllSubscriptionPacks = false,
  surface = 'light'
}: {
  payload?: RafflePaymentPayload;
  mode: CheckoutMode;
  pack: PackItem;
  onPaid: (args: {
    mode: CheckoutMode;
    paymentIntentId?: string;
    bonusSelected?: boolean;
    accountCreated?: boolean;
  }) => void;
  subDiscount: number;
  discountAllSubscriptionPacks?: boolean;
  /** Which palette to paint. Defaults to the original light one. */
  surface?: 'light' | 'dark';
}) {
  const c = SURFACES[surface];
  const b = BONUS_SURFACES[surface];
  const stripe = useStripe();
  const elements = useElements();
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [loading, setLoading] = useState(false);
  const [acceptTnC, setAcceptTnC] = useState(false);
  const [enableBonus, setEnableBonus] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  let entry: EntryFormValues;
  let raffleUnlockToken: string | null | undefined;

  if (payload) {
    entry = payload.entry;
    raffleUnlockToken = payload.raffleUnlockToken;
  }

  const hasUnlock = !!raffleUnlockToken;
  const signupCodeBonusEntries = Number(payload?.signupCodeBonusEntries || 0);
  const signupCodeBonusSource = payload?.signupCodeBonusSource;
  const signupSource = payload?.signupSource || 'giveaway';

  const basePrice = Number(pack.price ?? 0);
  const bonusPrice = Number(pack.bonus?.price ?? 0);

  const totalPrice = useMemo(() => {
    if (!pack.bonus) return basePrice;
    return basePrice + (enableBonus ? bonusPrice : 0);
  }, [basePrice, bonusPrice, enableBonus, pack.bonus]);

  const submit = async () => {
    if (!stripe || !elements) return;

    if (!acceptTnC) {
      showSnackbar('Please accept the terms and conditions.', 'error');
      return;
    }

    setLoading(true);

    try {
      const email = (entry.email || '').trim().toLowerCase();
      const attemptId = crypto.randomUUID();
      const returnUrl = window.location.href;
      const metaEventId = createMetaRegistrationEventId();
      const metaEventSourceUrl = getMetaEventSourceUrl();
      /* Also remembered, not just tracked: the confirmation page needs to know
       * whether an account was really created before it tells someone their login
       * details are in their inbox. The server only emails a NEW registration. */
      let accountCreated = false;
      const trackRegistrationIfCreated = (response: any) => {
        if (!response?.registrationCreated) return;
        accountCreated = true;

        // First-party acquisition event — fires whether or not the ad pixel is on.
        track('auth.signup', { source: 'giveaway', mode });

        if (!META_PIXEL_ID) return;
        loadMetaPixel(META_PIXEL_ID);
        trackCompleteRegistration({ eventId: metaEventId });
      };

      const firstName = entry.firstName.trim();
      const lastName = entry.lastName.trim();
      const isGiveawayEligible = entry.eligible === 'yes';

      const { error: submitError } = await elements.submit();
      if (submitError) {
        showSnackbar(
          submitError.message || 'Failed to submit details.',
          'error'
        );
        setLoading(false);
        return;
      }

      if (mode === 'subscription') {
        const si = await post(
          '/checkout/create-trial-setup-intent',
          hasUnlock
            ? { raffleUnlockToken, email }
            : {
                email,
                firstName,
                lastName,
                country: entry.countryName,
                isGiveawayEligible,
                phone: entry.phone,
                state: entry.stateName || entry.stateIso2,
                referralCode: '',
                metaEventId,
                metaEventSourceUrl,
                signupSource
              },
          true
        );

        trackRegistrationIfCreated(si);

        const { error: setupErr, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret: si.clientSecret,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        });

        if (setupErr) {
          trackError('giveaway.payment', setupErr, {
            mode,
            kind: 'subSetupIntent'
          });
          showSnackbar(setupErr.message || 'Card setup failed.', 'error');
          setLoading(false);
          return;
        }

        const pmId = setupIntent?.payment_method as string;

        const subBody = hasUnlock
          ? {
              raffleUnlockToken,
              email,
              packageId: Number(pack.id),
              firstMonthDiscountPercent: discountAllSubscriptionPacks
                ? subDiscount
                : undefined,
              signupCodeBonusEntries:
                signupCodeBonusEntries > 0 ? signupCodeBonusEntries : undefined,
              signupCodeBonusSource:
                signupCodeBonusEntries > 0 ? signupCodeBonusSource : undefined,
              paymentMethodId: pmId,
              attemptId,
              ipAddress: await publicIp()
            }
          : {
              packageId: Number(pack.id),
              hasBonus: false,
              firstMonthDiscountPercent: discountAllSubscriptionPacks
                ? subDiscount
                : undefined,
              signupCodeBonusEntries:
                signupCodeBonusEntries > 0 ? signupCodeBonusEntries : undefined,
              signupCodeBonusSource:
                signupCodeBonusEntries > 0 ? signupCodeBonusSource : undefined,
              email,
              firstName,
              lastName,
              country: entry.countryName,
              isGiveawayEligible,
              phone: entry.phone,
              state: entry.stateName || entry.stateIso2,
              referralCode: '',
              ipAddress: await publicIp(),
              paymentMethodId: pmId,
              attemptId,
              metaEventId,
              metaEventSourceUrl,
              signupSource
            };

        const res = await post(
          '/checkout/create-subscription-payment-intent',
          subBody,
          true
        );

        trackRegistrationIfCreated(res);

        const secret = res?.clientSecret ?? null;

        if (!secret || typeof secret !== 'string') {
          onPaid({ mode, accountCreated });
          setLoading(false);
          return;
        }

        if (secret.startsWith('pi_')) {
          await elements.submit();

          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });

          if (error) {
            trackError('giveaway.payment', error, {
              mode,
              kind: 'subConfirmPayment'
            });
            showSnackbar(
              error.message || 'Payment confirmation failed',
              'error'
            );
            setLoading(false);
            return;
          }

          onPaid({ mode, paymentIntentId: paymentIntent?.id, accountCreated });
          setLoading(false);
          return;
        }

        if (secret.startsWith('seti_')) {
          await elements.submit();

          const { error } = await stripe.confirmSetup({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });

          if (error) {
            trackError('giveaway.payment', error, {
              mode,
              kind: 'subConfirmSetup'
            });
            showSnackbar(error.message || 'Card setup failed', 'error');
            setLoading(false);
            return;
          }

          onPaid({ mode, accountCreated });
          setLoading(false);
          return;
        }

        onPaid({ mode, accountCreated });
        setLoading(false);
        return;
      }

      const standaloneBody = hasUnlock
        ? {
            raffleUnlockToken,
            email,
            packageId: Number(pack.id),
            hasBonus: Boolean(pack.bonus) && enableBonus,
            attemptId,
            ipAddress: await publicIp()
          }
        : {
            packageId: Number(pack.id),
            hasBonus: Boolean(pack.bonus) && enableBonus,
            email,
            firstName,
            lastName,
            country: entry.countryName,
            isGiveawayEligible,
            phone: entry.phone,
            state: entry.stateName || entry.stateIso2,
            referralCode: '',
            ipAddress: await publicIp(),
            paymentMethodId: null,
            attemptId,
            metaEventId,
            metaEventSourceUrl,
            signupSource
          };

      const resData = await post(
        '/checkout/create-standalone-payment-intent',
        standaloneBody,
        true
      );

      trackRegistrationIfCreated(resData);

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: resData.clientSecret,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required'
      });

      if (error) {
        trackError('giveaway.payment', error, {
          mode,
          kind: 'standaloneConfirmPayment'
        });
        showSnackbar(error.message || 'Payment failed.', 'error');
        setLoading(false);
        return;
      }

      // The upsell that follows charges against this token, not the intent id.
      rememberUpsellToken(paymentIntent?.id, resData?.upsellToken);

      onPaid({
        mode,
        accountCreated,
        paymentIntentId: paymentIntent?.id,
        bonusSelected: Boolean(pack.bonus) && enableBonus
      });
      setLoading(false);
    } catch (e: any) {
      trackError('giveaway.payment', e, { mode, kind: 'exception' });
      /* useNetwork throws a plain Error carrying the server's message; `.response`
       * is an axios shape that never arrives here, so this used to fall through to
       * 'Payment error.' and hide what the server actually said — including the
       * "you already have a subscription" and card refusals this flow relies on. */
      showSnackbar(e?.message || 'Payment error.', 'error');
      setLoading(false);
    }
  };

  const showBonus = mode === 'standalone' && Boolean(pack.bonus);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${c.border}`,
        background: c.shell
      }}
    >
      {/* Top title */}
      <Typography
        sx={{
          fontWeight: 950,
          fontSize: { xs: 28, sm: 32 },
          color: c.text,
          mb: 0.75,
          textAlign: 'center',
          lineHeight: 1.1
        }}
      >
        {pack.title}
      </Typography>

      <Typography
        sx={{
          textAlign: 'center',
          fontSize: 16,
          fontWeight: 700,
          color: c.muted,
          mb: 2.5
        }}
      >
        Complete your secure checkout below
      </Typography>

      {/* Bonus upsell */}
      {showBonus && (
        <Paper sx={{ ...b.card, mb: 2, p: { xs: 2.2, sm: 2.6 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.9,
                borderRadius: 999,
                border: '1px solid rgba(36, 191, 189, 0.25)',
                background: 'rgba(36, 191, 189, 0.10)'
              }}
            >
              <LocalOfferIcon
                sx={{ fontSize: 18, color: 'rgba(124,255,250,0.95)' }}
              />
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 950,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: b.heading
                }}
              >
                Bonus add-on
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 2
            }}
          >
            <Chip
              icon={<BoltIcon />}
              label="Boost odds"
              sx={{
                fontWeight: 900,
                color: b.text,
                background: b.chipFill,
                border: b.edge
              }}
            />
            <Chip
              icon={<TrendingUpIcon />}
              label="One-click add"
              sx={{
                fontWeight: 900,
                color: b.text,
                background: b.chipFill,
                border: b.edge
              }}
            />
            <Chip
              icon={<StarsIcon />}
              label="Best value"
              sx={{
                fontWeight: 900,
                color: 'rgba(0,0,0,0.85)',
                background: 'rgba(124,255,250,0.85)'
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2
            }}
          >
            <Box sx={b.tile}>
              <Typography
                sx={{
                  color: b.muted,
                  fontWeight: 800,
                  fontSize: 16
                }}
              >
                Extra Cards
              </Typography>
              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: { xs: 30, md: 34 },
                  fontWeight: 1000,
                  letterSpacing: '-0.03em',
                  color: b.text
                }}
              >
                +{pack.bonus!.extraCards}
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  color: b.muted,
                  fontWeight: 700,
                  fontSize: 16
                }}
              >
                Added to your rewards.
              </Typography>
            </Box>

            <Box sx={b.tile}>
              <Typography
                sx={{
                  color: b.muted,
                  fontWeight: 800,
                  fontSize: 16
                }}
              >
                Extra Entries
              </Typography>
              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: { xs: 30, md: 34 },
                  fontWeight: 1000,
                  letterSpacing: '-0.03em',
                  color: b.text
                }}
              >
                +{pack.bonus!.extraEntries}
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  color: b.muted,
                  fontWeight: 700,
                  fontSize: 16
                }}
              >
                More chances to win.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2.2, borderColor: b.rule }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              alignItems: 'center',
              gap: { xs: 2, md: 3 }
            }}
          >
            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 950,
                  color: b.text
                }}
              >
                Add bonus for{' '}
                <span style={{ color: b.price }}>
                  ${money(pack.bonus!.price)}
                </span>
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  color: b.muted,
                  fontWeight: 700,
                  fontSize: 16
                }}
              >
                You can only add this before checkout.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.4,
                width: '100%',
                maxWidth: '100%',
                minHeight: 92,
                px: { xs: 1.2, md: 1.4 },
                py: { xs: 1.1, md: 1.2 },
                borderRadius: 4,
                border: b.edge,
                background: enableBonus ? b.toggleFillOn : b.toggleFill,
                justifySelf: { xs: 'stretch', md: 'stretch' }
              }}
            >
              <Switch
                checked={enableBonus}
                onChange={(e) => setEnableBonus(e.target.checked)}
                sx={b.toggle}
              />

              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 16, md: 17 },
                    fontWeight: 950,
                    lineHeight: 1.1,
                    color: enableBonus ? b.toggleTextOn : b.text
                  }}
                >
                  {enableBonus ? 'Bonus added' : 'Add bonus'}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: 13, md: 14 },
                    fontWeight: 700,
                    color: b.toggleHint
                  }}
                >
                  {enableBonus
                    ? `Included for +A$${money(pack.bonus!.price)}`
                    : `Toggle to include for +A$${money(pack.bonus!.price)}`}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                px: 2.2,
                py: 1,
                borderRadius: 999,
                border: '1px solid rgba(36, 191, 189, 0.25)',
                background: 'rgba(36, 191, 189, 0.10)'
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 950,
                  color: b.text
                }}
              >
                Total:{' '}
                <span style={{ color: b.price }}>A${money(totalPrice)}</span>
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Secure payment shell */}
      <Box
        sx={{
          mt: 2,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: `1px solid ${c.border}`,
          background: c.panel,
          boxShadow: c.shadow
        }}
      >
        {/* Trust row */}
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.4,
            borderRadius: 2.5,
            border: `1px solid ${c.borderSoft}`,
            background: c.inset
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon
                sx={{ fontSize: 20, color: 'rgba(124,255,250,0.92)' }}
              />
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 900,
                  color: c.text,
                  lineHeight: 1.2
                }}
              >
                Secure payment powered by Stripe
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              gap: 1.25,
              flexWrap: 'wrap'
            }}
          >
            <Box
              component="img"
              src={visaIcon}
              alt="Visa"
              sx={{
                height: { xs: 48, md: 72, lg: 96 },
                width: 'auto',
                display: 'block'
              }}
            />

            <Box
              component="img"
              src={mastercardIcon}
              alt="Mastercard"
              sx={{
                height: { xs: 48, md: 72, lg: 96 },
                width: 'auto',
                display: 'block'
              }}
            />

            <Box
              component="img"
              src={stripeIcon}
              alt="Stripe"
              sx={{
                height: { xs: 48, md: 72, lg: 96 },
                width: 'auto',
                display: 'block'
              }}
            />
          </Box>

          <Box
            sx={{
              mt: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.8
            }}
          >
            <VerifiedUserIcon sx={{ fontSize: 18, color: c.mutedIcon }} />
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: c.mutedStrong,
                textAlign: 'center',
                lineHeight: 1.3
              }}
            >
              Your payment details are encrypted and securely processed by
              stripe.
            </Typography>
          </Box>
        </Box>

        {/* Stripe element frame */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2.5,
            border: `1px solid ${c.border}`,
            background: c.panel
          }}
        >
          <PaymentElement
            options={{ paymentMethodOrder: ['card'] }}
            onChange={(event) => {
              setPaymentComplete(event.complete);
            }}
          />
        </Box>

        <Typography
          sx={{
            mt: 1.3,
            textAlign: 'center',
            fontSize: 16,
            fontWeight: 700,
            color: c.faint,
            lineHeight: 1.3
          }}
        >
          We never store your full card details on our servers.
        </Typography>
      </Box>

      {/* Terms */}
      <FormControlLabel
        sx={{
          mt: 2,
          '& .MuiFormControlLabel-label': {
            color: c.text,
            fontWeight: 700
          }
        }}
        control={
          <Checkbox
            checked={acceptTnC}
            onChange={(e) => setAcceptTnC(e.target.checked)}
            sx={{
              color: c.checkbox,
              '& .MuiSvgIcon-root': {
                fontSize: 28
              },
              '&.Mui-checked': {
                color: c.checkboxOn
              }
            }}
          />
        }
        label="I accept the terms and conditions."
      />

      {/* CTA */}
      <Box
        sx={{
          mt: 1,
          p: 1.4,
          borderRadius: 2.5,
          border: `1px solid ${c.borderSoft}`,
          background: c.panel
        }}
      >
        <Button
          fullWidth
          onClick={submit}
          disabled={!stripe || loading || !acceptTnC || !paymentComplete}
          sx={{
            height: 54,
            borderRadius: 999,
            fontSize: 17,
            fontWeight: 950,
            color: 'rgba(0,0,0,0.85)',
            background:
              'linear-gradient(90deg, rgba(124,255,250,0.35) 0%, rgba(124,255,250,0.95) 100%)',
            opacity: !acceptTnC || !paymentComplete ? 0.45 : 1,
            cursor: !acceptTnC || !paymentComplete ? 'not-allowed' : 'pointer',
            boxShadow: '0 16px 40px rgba(0,0,0,0.28)',
            '&:hover': {
              background:
                'linear-gradient(90deg, rgba(124,255,250,0.45) 0%, rgba(124,255,250,1) 100%)'
            },
            '&.Mui-disabled': {
              color: '#475569',
              background: alpha('#7CFFFA', 0.45),
              boxShadow: 'none'
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'rgba(0,0,0,0.75)' }} />
          ) : mode === 'subscription' ? (
            'Start Subscription'
          ) : (
            'Tap to continue'
          )}
        </Button>

        {(!acceptTnC || !paymentComplete) && (
          <Typography
            sx={{
              mt: 1.1,
              textAlign: 'center',
              fontSize: 16,
              fontWeight: 700,
              color: c.hint,
              lineHeight: 1.3
            }}
          >
            {!acceptTnC
              ? 'Please accept the terms and conditions to continue.'
              : 'Please complete your payment details to continue.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
